import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Link } from "@mui/material";
import BrandGlow from "components/common/BrandGlow";
import Logo from "components/styled/Logo";

/** The headline the email, sign-in and sign-up steps share. */
export const AUTH_HERO_TITLE = "Smarter care starts here";

/**
 * The frame around every full-page sign-in screen (AuthPage, LinkDevicePage): the full
 * logo top left, a dim glow in the corners (BrandGlow) behind a centered column, and the
 * hero type. `glow={false}` drops the glow for the quieter screens after sign-in
 * (verify email, onboarding).
 */
const AuthPageShell = ({ children, logoLinked = true, glow = true }) => (
  <Box
    sx={{
      position: "relative",
      // Keeps BrandGlow's negative z-index behind the content but above the page.
      isolation: "isolate",
      minHeight: "100dvh",
      overflow: "hidden",
      bgcolor: "background.default",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      // Truly centered, as Grok's sign-in is: steps differ in height, so the form
      // shifts a little between them, which reads as calmer than a top-heavy page.
      // The vertical padding keeps it clear of the logo on short screens.
      justifyContent: "center",
      py: 9,
      px: 2,
      // Inputs stay solid over the glow so what's typed reads cleanly.
      "& .MuiOutlinedInput-root": { bgcolor: "background.default" },
      // The hero heading: a size down on phones, and on wide screens it runs past the
      // form's width so it sits on one line. Balanced wrapping keeps a two-line
      // heading from leaving one word alone.
      "& .MuiTypography-h3": {
        typography: { xs: "h4", sm: "h3" },
        textWrap: "balance",
        mx: { md: -16 },
      },
      "& .MuiTypography-h3 + .MuiTypography-body1": { textWrap: "balance" },
    }}
  >
    {/* The full logo, top left, the way most sign-in pages carry their mark. */}
    <Box
      sx={{
        position: "absolute",
        top: { xs: 16, sm: 24 },
        left: { xs: 16, sm: 32 },
      }}
    >
      {logoLinked ? (
        <Link component={RouterLink} to="/" underline="none" aria-label="Fusion MSI home">
          <Logo />
        </Link>
      ) : (
        <Logo />
      )}
    </Box>

    {/* In the page's corners, not around the form, so it stays put when a step's
        content grows (the QR code loading in). */}
    {glow && <BrandGlow preset="auth" />}

    <Box sx={{ position: "relative", width: "100%", maxWidth: 440 }}>
      {children}
    </Box>
  </Box>
);

export default AuthPageShell;
